import { DiscountGroup, isDiscountActive } from '@/store/use-capital-store';
import { cn } from '@/utils/cn';
import { centsToDollars, formatNumber } from '@/utils/value-format';

import { CapitalPlan } from '../api/get-capital-plans';
import {
  AccountOption,
  TableRowConfig,
} from '../utils/vest-capital-table.types';

// Transform API data to AccountOption format
export const transformCapitalPlansToAccountOptions = (
  capitalPlans: CapitalPlan[],
  appliedDiscountGroup: DiscountGroup | null,
  t: (key: string, options?: any) => string,
): AccountOption[] => {
  return capitalPlans
    .sort((a, b) => a.unitAmount - b.unitAmount)
    .map((plan) => {
      const accountSize = centsToDollars(plan.metadata.account_size);
      const step1Goal = centsToDollars(plan.metadata.step_1_goal);
      const unitAmount = centsToDollars(plan.unitAmount);
      const appliedDiscount = appliedDiscountGroup?.discounts.find(
        (discount) => discount.priceId === plan.priceId,
      );
      const discountPct = isDiscountActive(appliedDiscount)
        ? appliedDiscount!.discountPct
        : 0;
      const discountedUnitAmount =
        discountPct > 0
          ? Math.round(unitAmount * (1 - discountPct / 100) * 100) / 100
          : unitAmount;

      return {
        title: plan.name.split('|')[1]?.trim() ?? '',
        size: `$${formatNumber(accountSize)}`,
        profitSplit: formatNumber(Number(plan.metadata.max_profit_split), {
          style: 'percent',
          digits: 0,
        }),
        step1Goal: `$${formatNumber(step1Goal)}`,
        maxDailyLoss: `${plan.metadata.max_daily_loss}%`,
        maxDrawdown: `${formatNumber((Number(plan.metadata.max_drawdown) * accountSize) / 100, { style: 'currency', digits: 0 })}`,
        leverage: t('vestCapital.upTo5x', {
          leverage: plan.metadata.max_leverage,
        }),
        priceId: plan.priceId,
        evaluationFee: (
          <>
            <span
              className={cn(
                'text-3xl font-medium',
                discountedUnitAmount < unitAmount
                  ? 'text-muted-foreground line-through'
                  : '',
              )}
            >
              ${unitAmount.toFixed(2)}
            </span>
            {discountedUnitAmount < unitAmount && (
              <span className="text-3xl font-medium">
                {' '}
                ${discountedUnitAmount.toFixed(2)}
              </span>
            )}
          </>
        ),
      };
    });
};

export const tableRows: TableRowConfig[] = [
  {
    key: 'step1Goal',
    labelKey: 'vestCapital.step1Goal',
    tooltipKey: 'vestCapital.step1GoalTooltip',
  },
  {
    key: 'profitSplit',
    labelKey: 'vestCapital.profitSplit',
    tooltipKey: 'vestCapital.profitSplitTooltip',
  },
  {
    key: 'leverage',
    labelKey: 'vestCapital.leverage',
    tooltipKey: 'vestCapital.leverageTooltip',
    mobileOnly: true,
  },
  {
    key: 'maxDailyLoss',
    labelKey: 'vestCapital.maxDailyLoss',
    tooltipKey: 'vestCapital.maxDailyLossTooltip',
    mobileOnly: true,
  },
  {
    key: 'maxDrawdown',
    labelKey: 'vestCapital.maxDrawdown',
    tooltipKey: 'vestCapital.maxDrawdownTooltip',
  },
  {
    key: 'evaluationFee',
    labelKey: 'vestCapital.evaluationFee',
    tooltipKey: 'vestCapital.evaluationFeeTooltip',
  },
];
